import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Share2, Globe, Sparkles, BookMarked, MessageCircle } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CampaignCard } from "../components/CampaignCard";
import { PublicMediaGallery } from "../components/MediaGallery";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

export default function AuthorPublicPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [author, setAuthor] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    Promise.all([
      api.get(`/authors/${id}`),
      api.get(`/authors/${id}/campaigns`),
    ])
      .then(([a, c]) => { setAuthor(a.data); setCampaigns(c.data); })
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error(apiError(e));
      })
      .finally(() => setLoading(false));
  }, [id]);

  const share = async () => {
    const url = window.location.origin + `/authors/${id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: author?.display_name, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch { /* user cancelled */ }
  };

  const startChat = async () => {
    if (!user) { nav("/login"); return; }
    if (user.id === id) { toast.error("That's you!"); return; }
    try {
      const { data } = await api.post("/conversations", { other_user_id: id });
      nav(`/messages?c=${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const socials = [
    { key: "website_url", label: "Website" },
    { key: "goodreads_url", label: "Goodreads" },
    { key: "amazon_author_url", label: "Amazon" },
    { key: "substack_url", label: "Substack" },
    { key: "instagram_url", label: "Instagram" },
    { key: "tiktok_url", label: "TikTok" },
    { key: "youtube_url", label: "YouTube" },
    { key: "twitter_url", label: "X" },
    { key: "facebook_url", label: "Facebook" },
    { key: "threads_url", label: "Threads" },
    { key: "bluesky_url", label: "Bluesky" },
  ];

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-16" style={{ color: "var(--ink-muted)" }}>Loading…</div>;
  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="paper-card p-10">
          <h1 className="rainbow-text text-3xl mb-3">Author not found</h1>
          <p className="text-sm mb-6" style={{ color: "var(--ink-soft)" }}>This author page doesn't exist or has been removed.</p>
          <Link to="/campaigns"><Button className="btn-press btn-rainbow" data-testid="author-notfound-browse-btn">Browse campaigns</Button></Link>
        </div>
      </div>
    );
  }

  const photo = author.photo_url || "https://images.unsplash.com/photo-1598024055266-e772a5f8c128?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="paper-card p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start" data-testid="author-public-card">
        <img
          src={photo}
          alt={author.display_name}
          className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl object-cover flex-shrink-0"
          style={{ border: "1px solid var(--border-soft)" }}
          data-testid="author-public-photo"
        />
        <div className="flex-1 min-w-0">
          <div className="overline mb-1">Author</div>
          <h1 className="rainbow-text text-3xl sm:text-4xl leading-tight" data-testid="author-public-name">
            {author.display_name}
          </h1>

          {author.pen_names?.length > 0 && (
            <div className="mt-3">
              <span className="text-xs" style={{ color: "var(--ink-muted)" }}>Also writes as</span>
              <div className="flex flex-wrap gap-2 mt-1" data-testid="author-public-pennames">
                {author.pen_names.map((p) => (
                  <Badge key={p} variant="outline" className="border" style={{ borderColor: "var(--border-soft)" }}>{p}</Badge>
                ))}
              </div>
            </div>
          )}

          {author.bio && (
            <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }} data-testid="author-public-bio">
              {author.bio}
            </p>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-2">
            {socials.filter((s) => author[s.key]).map((s) => (
              <a
                key={s.key}
                href={author[s.key]}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border hover:opacity-80"
                style={{ borderColor: "var(--border-soft)" }}
                data-testid={`author-public-${s.key}`}
              >
                <Globe size={14} /> {s.label}
              </a>
            ))}
            {author.auto_accept_enabled && (
              <Badge className="status-auto border"><Sparkles size={12} className="mr-1" /> Auto-Accept Enabled</Badge>
            )}
            <div className="ml-auto flex gap-2">
              {user && user.id !== id && (
                <Button onClick={startChat} variant="outline" className="btn-press" data-testid="author-public-message-btn">
                  <MessageCircle size={14} className="mr-1.5" /> Message
                </Button>
              )}
              <Button
                onClick={share}
                className="btn-press btn-rainbow rounded-full"
                data-testid="author-public-share-btn"
              >
                <Share2 size={14} className="mr-1.5" /> Share
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-end justify-between mt-10 mb-4">
        <div>
          <div className="overline mb-1">Live campaigns</div>
          <h2 className="rainbow-text text-2xl">Open ARCs from {author.display_name}</h2>
        </div>
        <div className="text-xs" style={{ color: "var(--ink-muted)" }} data-testid="author-public-campaign-count">
          {campaigns.length} active
        </div>
      </div>

      {campaigns.length === 0 ? (
        <div className="paper-card p-10 text-center" data-testid="author-public-empty">
          <BookMarked size={28} className="mx-auto mb-3 rainbow-text" />
          <div className="rainbow-text text-xl mb-1">No live campaigns yet</div>
          <p className="text-sm" style={{ color: "var(--ink-soft)" }}>Check back soon — new ARCs land here first.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="author-public-grid">
          {campaigns.map((c) => <CampaignCard key={c.id} campaign={c} />)}
        </div>
      )}

      <PublicMediaGallery userId={id} />
    </div>
  );
}
